import { useState, useMemo } from "react";
import Button from "./common/Button";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import * as utils from "../utils/utils";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import { Badge } from "./common/Badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";
import { useData } from "../lib/DataContext";
import {
  Search,
  Stethoscope,
  Calendar,
  DollarSign,
  ArrowLeft,
  Filter,
  X,
  User,
  Phone,
  Mail,
  Clock,
  Check,
  Building2,
  Users,
  MapPin,
  CircleCheck,
  CircleX,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import useBrandTranslation from "../hooks/useBrandTranslation";
import useActingPatient from "../hooks/useActingPatient";
import {
  HEALTH_INSURANCES,
  acceptsInsurance,
  canonicalInsuranceName,
  getAcceptedInsurances,
  sameInsurance,
} from "../utils/healthInsurances";
import {
  distanceKm,
  formatDistanceKm,
  getDoctorLocation,
  getHomeLocation,
} from "../utils/geo";

const SPECIALIZATION_DEFS = [
  { key: "cardiology", es: "Cardiología" },
  { key: "dermatology", es: "Dermatología" },
  { key: "endocrinology", es: "Endocrinología" },
  { key: "gastroenterology", es: "Gastroenterología" },
  { key: "gynecology", es: "Ginecología" },
  { key: "generalMedicine", es: "Medicina General" },
  { key: "neurology", es: "Neurología" },
  { key: "ophthalmology", es: "Oftalmología" },
  { key: "orthopedics", es: "Ortopedia" },
  { key: "pediatrics", es: "Pediatría" },
  { key: "psychiatry", es: "Psiquiatría" },
  { key: "traumatology", es: "Traumatología" },
  { key: "urology", es: "Urología" },
];

function getSpecializationLabel(key, t) {
  const def = SPECIALIZATION_DEFS.find((item) => item.key === key);
  return t(
    `app.search.specializations.${key}`,
    def?.es || key
  );
}

function doctorMatchesSpecialization(doctor, key, t) {
  if (!key || key === "all") return true;
  const def = SPECIALIZATION_DEFS.find((item) => item.key === key);
  if (!def) return false;
  const localized = getSpecializationLabel(key, t);
  return (
    doctor.specialization === def.es || doctor.specialization === localized
  );
}

function localizeDoctorSpecialization(specialization, t) {
  if (!specialization) return specialization;
  const def = SPECIALIZATION_DEFS.find((item) => item.es === specialization);
  if (!def) return specialization;
  return getSpecializationLabel(def.key, t);
}

export default function ProfessionalSearch() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const { profiles, loading, sharedSchedules, secretaries, getSchedulesByDoctor, professionalsAssociations } = useData();
  const navigation = useNavigate();
  const { acting, isSelf } = useActingPatient();

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialization, setSelectedSpecialization] = useState("");
  const [selectedInsurance, setSelectedInsurance] = useState("");
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("distance"); // distance, name, specialization

  // Paciente para el que se busca (titular o familiar activo). La cobertura es
  // la del sujeto; la ubicación, el domicilio del titular cargado en "Mi salud"
  // (los familiares no tienen dirección propia).
  const holder = useMemo(
    () => profiles.find((p) => p.id === user?.id) || user,
    [profiles, user]
  );
  const subject = useMemo(
    () => (isSelf ? holder : profiles.find((p) => p.id === acting.id) || holder),
    [isSelf, holder, profiles, acting.id]
  );
  const patientInsurance = subject?.health_insurance_name?.trim() || "";
  const patientLocation = useMemo(() => getHomeLocation(holder), [holder]);
  const patientProfileLink = !isSelf
    ? "/profile?tab=dependents"
    : user?.role === "doctor"
      ? "/profile?tab=patientData"
      : "/profile";

  // Obtener solo los médicos
  const doctors = useMemo(() => {
    return profiles.filter(
      (profile) =>
        utils.isBookableDoctor(profile) && profile.id !== user?.id
    );
  }, [profiles, user?.id]);

  // Obtener instituciones disponibles (de secretaries con role "institution")
  const availableInstitutions = useMemo(() => {
    const institutions = secretaries
      .filter((s) => s.role === "institution")
      .map((s) => ({
        id: s.id,
        name: utils.getOrganizationDisplayName(s),
      }))
      .filter((inst) => inst.name)
      .filter((inst, index, self) =>
        index === self.findIndex((i) => i.name === inst.name)
      ) // Eliminar duplicados por nombre
      .sort((a, b) => a.name.localeCompare(b.name));
    return institutions;
  }, [secretaries]);

  // Datos derivados por profesional: institución, lugar de atención, distancia
  // al domicilio del paciente y si atiende su cobertura.
  const doctorInfo = useMemo(() => {
    const map = new Map();
    for (const doctor of doctors) {
      const institution = utils.findDoctorInstitution(doctor.id, {
        sharedSchedules,
        secretaries,
        getSchedulesByDoctor,
      });
      const location = getDoctorLocation(doctor, institution);
      const accepted = getAcceptedInsurances(doctor, institution);
      map.set(doctor.id, {
        institution: institution
          ? { id: institution.id, name: utils.getOrganizationDisplayName(institution) }
          : null,
        location,
        distance: distanceKm(patientLocation.coords, location.coords),
        accepted,
        acceptsPatientInsurance: acceptsInsurance(accepted, patientInsurance),
      });
    }
    return map;
  }, [doctors, sharedSchedules, secretaries, getSchedulesByDoctor, patientLocation, patientInsurance]);

  // Opciones del filtro de cobertura: la del paciente primero (después de "Todas").
  const patientInsuranceOption = patientInsurance
    ? canonicalInsuranceName(patientInsurance)
    : "";
  const insuranceOptions = useMemo(
    () =>
      HEALTH_INSURANCES.filter(
        (name) => !patientInsuranceOption || !sameInsurance(name, patientInsuranceOption)
      ),
    [patientInsuranceOption]
  );

  // Aplicar filtros y búsqueda
  const filteredDoctors = useMemo(() => {
    let filtered = [...doctors];

    // Filtro por término de búsqueda (nombre, apellido, especialización)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (doctor) =>
          doctor.name.toLowerCase().includes(term) ||
          doctor.last_name.toLowerCase().includes(term) ||
          (doctor.specialization &&
            doctor.specialization.toLowerCase().includes(term)) ||
          doctor.email.toLowerCase().includes(term)
      );
    }

    // Filtro por especialización
    if (selectedSpecialization && selectedSpecialization !== "all") {
      filtered = filtered.filter((doctor) =>
        doctorMatchesSpecialization(doctor, selectedSpecialization, t)
      );
    }

    // Filtro por obra social / prepaga
    if (selectedInsurance && selectedInsurance !== "all") {
      filtered = filtered.filter((doctor) =>
        acceptsInsurance(doctorInfo.get(doctor.id)?.accepted, selectedInsurance)
      );
    }

    // Filtro por institución
    if (selectedInstitution && selectedInstitution !== "all") {
      filtered = filtered.filter(
        (doctor) => doctorInfo.get(doctor.id)?.institution?.id === selectedInstitution
      );
    }

    const byName = (a, b) =>
      `${a.name} ${a.last_name}`.localeCompare(`${b.name} ${b.last_name}`);

    // Ordenamiento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "distance": {
          // Más cercanos primero; sin distancia conocida, al final por nombre.
          const da = doctorInfo.get(a.id)?.distance;
          const db = doctorInfo.get(b.id)?.distance;
          if (da != null && db != null) return da - db;
          if (da != null) return -1;
          if (db != null) return 1;
          return byName(a, b);
        }
        case "name":
          return byName(a, b);
        case "specialization":
          return (a.specialization || "").localeCompare(b.specialization || "");
        default:
          return 0;
      }
    });

    return filtered;
  }, [
    doctors,
    searchTerm,
    selectedSpecialization,
    selectedInsurance,
    selectedInstitution,
    sortBy,
    doctorInfo,
    t,
  ]);

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedSpecialization("");
    setSelectedInsurance("");
    setSelectedInstitution("");
    setSortBy("distance");
  };

  // Contar filtros activos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchTerm.trim()) count++;
    if (selectedSpecialization && selectedSpecialization !== "all") count++;
    if (selectedInsurance && selectedInsurance !== "all") count++;
    if (selectedInstitution && selectedInstitution !== "all") count++;
    return count;
  }, [searchTerm, selectedSpecialization, selectedInsurance, selectedInstitution]);

  const handleBookAppointment = (doctor) => {
    // Navegar a la página de reserva con el médico preseleccionado
    navigation(`/patient/book?doctorId=${doctor.id}`);
  };

  if (!user || !utils.canActAsPatient(user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.appointments.restrictedAccess", "Acceso Restringido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              {t("app.search.patientsOnly", "Esta sección es solo para pacientes.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t("app.search.pageTitle", "Buscar Profesional")}
          </h1>
          <p className="text-gray-600">
            {t(
              "app.search.pageSubtitle",
              "Encuentra el médico ideal para tu consulta"
            )}
          </p>
        </div>

        {/* Barra de búsqueda y filtros */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Búsqueda principal */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder={t("app.search.searchProfessional", "Buscar por nombre, apellido, especialidad o email...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 h-12 text-base mt-4"
                />
              </div>

              {/* Controles de filtros y ordenamiento */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    {t("app.search.filters", "Filtros")}
                    {activeFiltersCount > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {activeFiltersCount}
                      </Badge>
                    )}
                  </Button>

                  {activeFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      onClick={clearFilters}
                      className="flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      {t("app.search.clearFilters", "Limpiar filtros")}
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Label htmlFor="sort" className="text-sm font-medium">
                    {t("app.search.sortBy", "Ordenar por:")}
                  </Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-52">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="distance">
                        {t("app.search.sortDistance", "Ubicación (más cerca)")}
                      </SelectItem>
                      <SelectItem value="name">{t("app.search.sortName", "Nombre")}</SelectItem>
                      <SelectItem value="specialization">
                        {t("app.search.specialty", "Especialidad")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Panel de filtros expandible */}
              {showFilters && (
                <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="specialization">{t("app.search.specialty", "Especialidad")}</Label>
                    <Select
                      value={selectedSpecialization}
                      onValueChange={setSelectedSpecialization}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("app.search.allSpecialties", "Todas las especialidades")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          {t("app.search.allSpecialties", "Todas las especialidades")}
                        </SelectItem>
                        {SPECIALIZATION_DEFS.map((spec) => (
                          <SelectItem key={spec.key} value={spec.key}>
                            {getSpecializationLabel(spec.key, t)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="insurance">{t("app.search.insurance", "Obra social / Prepaga")}</Label>
                    <Select
                      value={selectedInsurance}
                      onValueChange={setSelectedInsurance}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("app.search.allInsurances", "Todas")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("app.search.allInsurances", "Todas")}</SelectItem>
                        {patientInsuranceOption && (
                          <SelectItem value={patientInsuranceOption}>
                            {t("app.search.myInsurance", "{{name}} (tu cobertura)", {
                              name: patientInsuranceOption,
                            })}
                          </SelectItem>
                        )}
                        {insuranceOptions.map((name) => (
                          <SelectItem key={name} value={name}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="institution">{t("app.register.institutionLabel", "Institución")}</Label>
                    <Select
                      value={selectedInstitution}
                      onValueChange={setSelectedInstitution}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("app.search.allInstitutions", "Todas las instituciones")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("app.search.allInstitutions", "Todas las instituciones")}</SelectItem>
                        {availableInstitutions.map((institution) => (
                          <SelectItem key={institution.id} value={institution.id}>
                            {institution.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Resultados */}
        <div className="space-y-4">
          {/* Contador de resultados */}
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              {filteredDoctors.length === 1
                ? t(
                    "app.search.professionalsFoundOne",
                    "{{count}} profesional encontrado",
                    { count: filteredDoctors.length }
                  )
                : t(
                    "app.search.professionalsFoundMany",
                    "{{count}} profesionales encontrados",
                    { count: filteredDoctors.length }
                  )}
              {searchTerm &&
                t("app.search.resultsForSearch", ' para "{{term}}"', {
                  term: searchTerm,
                })}
            </p>
          </div>

          {/* Lista de médicos */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6 mt-4">
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredDoctors.length === 0 ? (
            <Card>
              <CardContent className="text-center py-6">
                <Stethoscope className="w-16 h-16 text-gray-300 mx-auto my-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t("app.search.noProfessionalsFound", "No se encontraron profesionales")}
                </h3>
                <p className="text-gray-500 mb-4">
                  {t(
                    "app.search.adjustFiltersHint",
                    "Intenta ajustar tus filtros de búsqueda o buscar con otros términos."
                  )}
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  {t("app.search.clearFilters", "Limpiar filtros")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map((doctor) => (
                <DoctorCard
                  key={doctor.id}
                  doctor={doctor}
                  info={doctorInfo.get(doctor.id)}
                  patientInsurance={patientInsurance}
                  patientHasAddress={!!patientLocation.address}
                  patientProfileLink={patientProfileLink}
                  onBookAppointment={handleBookAppointment}
                  profiles={profiles}
                  professionalsAssociations={professionalsAssociations}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DoctorCard({
  doctor,
  info,
  patientInsurance,
  patientHasAddress,
  patientProfileLink,
  onBookAppointment,
  profiles,
  professionalsAssociations,
}) {
  const { t } = useBrandTranslation();
  const institutionInfo = info?.institution;
  const location = info?.location || { address: "", coords: null };

  // Función para obtener la información de la asociación
  const getAssociationInfo = () => {
    if (!professionalsAssociations || !profiles) {
      return null;
    }

    // Buscar la relación activa del doctor con una asociación
    const associationRelation = professionalsAssociations.find(
      pa => pa.professional_id === doctor.id && pa.status === "active"
    );

    if (!associationRelation) {
      return null;
    }

    // Buscar el perfil de la asociación
    const association = profiles.find(
      p => p.id === associationRelation.association_id && p.role === "association"
    );

    if (!association) {
      return null;
    }

    const associationName = utils.getOrganizationDisplayName(association);
    return {
      id: association.id,
      name: associationName
    };
  };

  const associationInfo = getAssociationInfo();

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-blue-600" />
              Dr. {doctor.name} {doctor.last_name}
            </CardTitle>

            {doctor.specialization && (
              <Badge variant="outline" className="mt-2">
                <Stethoscope className="w-3 h-3 mr-1" />
                {localizeDoctorSpecialization(doctor.specialization, t)}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Información de contacto */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Mail className="w-4 h-4" />
            <span>{doctor.email}</span>
          </div>

          {doctor.phone && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone className="w-4 h-4" />
              <span>{doctor.phone}</span>
            </div>
          )}
        </div>

        {/* Precio */}
        {!!doctor.private_appointment_price ? (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-green-600">
              ${doctor.private_appointment_price.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500">{t("app.search.perConsultation", "por consulta")}</span>
          </div>
        ) : <div className="h-6"></div>}

        {/* Información adicional */}
        <div className="pt-2 border-t">
          <div className="flex flex-col gap-2 text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              <span>
                {t("app.search.memberSince", "Miembro desde {{year}}", {
                  year: new Date(doctor.created_at).getFullYear(),
                })}
              </span>
            </div>
              <div className="flex flex-col items-start gap-2 text-blue-600 font-medium h-14">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                {t("app.search.hasPrivateSchedule", "Tiene agenda particular")}
              </span>
                {institutionInfo && (
                  <Link
                    to={`/institution/${institutionInfo.id}`}
                    className="flex items-center gap-2 hover:text-blue-800 hover:underline transition-colors"
                  >
                    <Building2 className="w-4 h-4" />
                    {t("app.search.scheduleAtInstitution", "Tiene agenda en {{name}}", {
                      name: institutionInfo.name,
                    })}
                  </Link>
                )}
                {/* {associationInfo && (
                <Link
                  to={`/association/${associationInfo.id}`}
                  className="flex items-center gap-2 hover:text-blue-800 hover:underline transition-colors"
                >
                  <Users className="w-4 h-4" />
                    Adherido a Asociación {associationInfo.name}
                  </Link>
                )} */}
              </div>
          </div>

          {/* Cobertura y ubicación respecto del paciente */}
          <div className="flex flex-col gap-2 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              {patientInsurance && info?.acceptsPatientInsurance ? (
                <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              ) : (
                <CircleX className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              )}
              {patientInsurance ? (
                <span title={
                  info?.acceptsPatientInsurance
                    ? t("app.search.acceptsInsurance", "Atiende por tu cobertura")
                    : t("app.search.notAcceptsInsurance", "No atiende por tu cobertura: solo turnos particulares")
                }>
                  {patientInsurance}
                </span>
              ) : (
                <Link to={patientProfileLink} className="hover:underline">
                  {t("app.search.addInsurance", "Debes agregar tu cobertura médica")}
                </Link>
              )}
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              {!location.address ? (
                <span className="text-gray-500">
                  {t("app.search.noDoctorAddress", "El profesional no suministró su dirección")}
                </span>
              ) : info?.distance != null ? (
                <span>
                  {t("app.search.distanceAt", "A {{km}} km, en {{address}}", {
                    km: formatDistanceKm(info.distance),
                    address: location.address,
                  })}
                </span>
              ) : (
                <span className="flex flex-col">
                  <span>{location.address}</span>
                  {!patientHasAddress && (
                    <Link to={patientProfileLink} className="text-xs text-gray-500 hover:underline">
                      {t("app.search.addAddress", "Debes agregar tu dirección para ver la distancia")}
                    </Link>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Botón de acción */}
        <Button onClick={() => onBookAppointment(doctor)} className="w-full">
          <Calendar className="w-4 h-4 mr-2" />
          {t("app.search.bookAppointment", "Reservar Turno")}
        </Button>
      </CardContent>
    </Card>
  );
}
